import { useTranslation } from 'react-i18next';
import type {
  AgreementStatus,
  AuditResult,
  CleanVerdict,
  ProjectHealth,
  ProjectStatus,
  SchedulingState,
  InfrastructureStatus,
  UserStatus,
  VerificationState,
  WorkflowStatus,
} from '@dashboard/contracts';

/**
 * Status is communicated three ways at once: a glyph, a translated label, and a
 * colour. Section 18 forbids colour alone, and the glyph also survives the
 * cases colour cannot -- monochrome print, forced-colours mode, and a
 * screenshot pasted into a ticket.
 */

const SERVICE_GLYPHS: Record<InfrastructureStatus, string> = {
  operational: '●',
  degraded: '▲',
  outage: '■',
  maintenance: '◆',
  unknown: '○',
};

const HEALTH_GLYPHS: Record<ProjectHealth, string> = {
  on_track: '●',
  at_risk: '▲',
  off_track: '■',
  unknown: '○',
};

export function InfrastructureStatusBadge({ status }: { status: InfrastructureStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${status}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {SERVICE_GLYPHS[status]}
      </span>
      {t(`status.${status}`)}
    </span>
  );
}

export function ProjectHealthBadge({ health }: { health: ProjectHealth }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${health}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {HEALTH_GLYPHS[health]}
      </span>
      {t(`projectHealth.${health}`)}
    </span>
  );
}

export function ProjectStatusLabel({ status }: { status: ProjectStatus }) {
  const { t } = useTranslation();
  return <span>{t(`projectStatus.${status}`)}</span>;
}

const SCHEDULING_STATE_GLYPHS: Record<SchedulingState, string> = {
  scheduled: '●',
  unscheduled: '▲',
  blocked: '■',
};

/** `scheduled`/`unscheduled`/`blocked` maps onto the same success/warning/danger
 * palette the other badges use, via `.status-badge--scheduled` and friends
 * declared alongside them in styles.css. */
export function SchedulingStateBadge({ state }: { state: SchedulingState }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${state}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {SCHEDULING_STATE_GLYPHS[state]}
      </span>
      {t(`schedule.schedulingState.${state}`)}
    </span>
  );
}

const USER_STATUS_GLYPHS: Record<UserStatus, string> = {
  active: '●',
  invited: '◆',
  disabled: '■',
};

export function UserStatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${status}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {USER_STATUS_GLYPHS[status]}
      </span>
      {t(`userStatus.${status}`)}
    </span>
  );
}

const AUDIT_RESULT_GLYPHS: Record<AuditResult, string> = {
  success: '●',
  failure: '■',
  denied: '▲',
};

/**
 * How an audited attempt ended.
 *
 * `denied` takes the warning palette rather than the danger one: a refusal is
 * the authorization layer working, and an audit log where every routine 403
 * reads as an error is one an administrator learns to skim past -- which is the
 * opposite of what it is for.
 */
export function AuditResultBadge({ result }: { result: AuditResult }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${result}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {AUDIT_RESULT_GLYPHS[result]}
      </span>
      {t(`audit.results.${result}`)}
    </span>
  );
}

export function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--workflow-${status}`}>
      {t(`schedule.workflowStatus.${status}`)}
    </span>
  );
}

/**
 * Progress needs both a bar and its number: the bar for scanning, the number
 * for anyone who cannot resolve a 3-pixel difference in fill length.
 */
export function ProgressCell({
  value,
  locale,
  label,
}: {
  value: number;
  locale: string;
  /** Names the bar for assistive technology; an unnamed progressbar is a WCAG failure. */
  label?: string;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div
        role="progressbar"
        aria-label={label ?? t('fields.progress')}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={new Intl.NumberFormat(locale, { style: 'percent' }).format(value / 100)}
        style={{
          flex: 1,
          minWidth: 40,
          height: 6,
          borderRadius: 3,
          background: 'var(--color-surface-muted)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: '100%',
            background: 'var(--color-primary)',
            borderRadius: 3,
          }}
        />
      </div>
      <span className="numeric" style={{ fontSize: '0.78rem', minWidth: '2.5rem' }}>
        {new Intl.NumberFormat(locale, { style: 'percent' }).format(value / 100)}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ fleet */

/**
 * Whether a vehicle counts as clean, on the date it was judged.
 *
 * Three glyphs for three verdicts, and the third is the one that earns the
 * component: `indeterminate` is not a shade of `not_clean`, it is "we cannot
 * tell", and a badge that rendered it as a paler red would tell a procurement
 * officer to go and find a cleaner lorry when what they need to find is a
 * missing CO2 figure.
 *
 * Reuses the sheet's existing modifiers rather than adding three of its own:
 * `success`, `failure` and `unknown` already mean exactly this.
 */
const VERDICT_GLYPHS: Record<CleanVerdict, string> = {
  clean: '●',
  not_clean: '■',
  indeterminate: '○',
};

const VERDICT_MODIFIERS: Record<CleanVerdict, string> = {
  clean: 'success',
  not_clean: 'failure',
  indeterminate: 'unknown',
};

export function CleanVerdictBadge({ verdict }: { verdict: CleanVerdict }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${VERDICT_MODIFIERS[verdict]}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {VERDICT_GLYPHS[verdict]}
      </span>
      {t(`fleet.verdict.${verdict}`)}
    </span>
  );
}

/** How far a declaration has been checked. `declared` is the ordinary state and
 *  is drawn as neutral, not as a warning: self-reported is not a defect. */
const VERIFICATION_GLYPHS: Record<VerificationState, string> = {
  declared: '○',
  verified: '●',
  disputed: '▲',
};

const VERIFICATION_MODIFIERS: Record<VerificationState, string> = {
  declared: 'neutral',
  verified: 'success',
  disputed: 'failure',
};

export function VerificationBadge({ state }: { state: VerificationState }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${VERIFICATION_MODIFIERS[state]}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {VERIFICATION_GLYPHS[state]}
      </span>
      {t(`fleet.verification.${state}`)}
    </span>
  );
}

const AGREEMENT_GLYPHS: Record<AgreementStatus, string> = {
  draft: '○',
  active: '●',
  closed: '■',
};

const AGREEMENT_MODIFIERS: Record<AgreementStatus, string> = {
  draft: 'unknown',
  active: 'active',
  closed: 'neutral',
};

export function AgreementStatusBadge({ status }: { status: AgreementStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${AGREEMENT_MODIFIERS[status]}`}>
      <span className="status-badge__glyph" aria-hidden="true">
        {AGREEMENT_GLYPHS[status]}
      </span>
      {t(`fleet.agreementStatus.${status}`)}
    </span>
  );
}
